import React, { useEffect } from "react";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import {
	Dialog,
	DialogTitle,
	DialogContent,
	DialogActions,
	Box,
} from "@mui/material";
import moment from "moment";
import { useSelector } from "react-redux";

import DropdownController from "../../components/inputs/DropdownController";
import DatePickerController from "../../components/inputs/DatePickerController";
import PATCloseButton from "../../components/Buttons/PATCloseButton";
import PATSaveButton from "../../components/Buttons/PATSaveButton";
import { isValidDateTest, maxLengthMessage } from "../../utils/functions";
import TextFieldController from "../../components/inputs/TextFieldController";

export const FUNDING_STATUSES = [
	{ value: "Active", label: "Active" },
	{ value: "Pending", label: "Pending" },
	{ value: "Expired", label: "Expired" },
	{ value: "Depleted", label: "Depleted" },
];

const fundingSourceSchema = yup.object().shape({
	title: yup
		.string()
		.trim()
		.required("Title is required")
		.max(100, maxLengthMessage(100)),
	amount: yup
		.number()
		.required("Amount is required")
		.min(0, "Amount cannot be negative")
		.typeError("Amount must be a number"),
	status: yup
		.string()
		.trim()
		.required("Status is required")
		.max(50, maxLengthMessage(50)),
	source: yup
		.string()
		.trim()
		.required("Source is required")
		.max(150, maxLengthMessage(150)),
	administrator: yup
		.string()
		.trim()
		.required("Administrator is required")
		.max(100, maxLengthMessage(100)),
	startDate: isValidDateTest("Start Date"),
	endDate: isValidDateTest("End Date"),
	description: yup.string().trim().max(500, maxLengthMessage(500)).nullable(),
});

const buildDefaults = (initialData) => ({
	title: initialData?.title || "",
	amount: initialData?.amount || "",
	status: initialData?.status || "",
	source: initialData?.source || "",
	administrator: initialData?.administrator || "",
	startDate: initialData?.startDate
		? moment(initialData.startDate).format("MM/DD/YYYY")
		: "",
	endDate: initialData?.endDate
		? moment(initialData.endDate).format("MM/DD/YYYY")
		: "",
	description: initialData?.description || "",
});

const AddEditFundingSourceDialog = ({
	open,
	onClose,
	onSave,
	initialData,
	viewOnly = false,
}) => {
	const user = useSelector((state) => state.user);

	const {
		control,
		handleSubmit,
		reset,
		formState: { errors, isValid },
	} = useForm({
		resolver: yupResolver(fundingSourceSchema),
		defaultValues: buildDefaults(initialData),
	});

	// Reset form when dialog opens with new data
	useEffect(() => {
		reset(buildDefaults(initialData));
	}, [initialData, reset]);

	// Reset on close
	useEffect(() => {
		if (!open) {
			reset();
		}
	}, [open, reset]);

	const onSubmit = (data) => {
		const payload = {
			title: data.title?.trim() || null,
			amount: data.amount || null,
			status: data.status || null,
			source: data.source?.trim() || null,
			administrator: data.administrator?.trim() || null,
			startDate: data.startDate
				? moment(data.startDate, "MM/DD/YYYY").format("YYYY-MM-DD")
				: null,
			endDate: data.endDate
				? moment(data.endDate, "MM/DD/YYYY").format("YYYY-MM-DD")
				: null,
			description: data.description?.trim() || null,
			agencyId: user?.userAgency || null,
		};

		onSave(payload);
		reset();
		onClose();
	};

	const dialogTitle = initialData
		? viewOnly
			? `View — ${initialData.title}`
			: `Edit — ${initialData.title}`
		: "New Funding Source";

	return (
		<Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
			<DialogTitle>{dialogTitle}</DialogTitle>

			<DialogContent
				sx={{
					pointerEvents: viewOnly ? "none" : undefined,
					opacity: viewOnly ? 0.75 : undefined,
				}}
			>
				<Box
					component="form"
					onSubmit={handleSubmit(onSubmit)}
					noValidate
				>
					<Box
						sx={{
							display: "grid",
							gridTemplateColumns: "repeat(2, 1fr)",
							gap: 2,
							pt: 1,
						}}
					>
						<Box sx={{ gridColumn: "1 / -1" }}>
							<TextFieldController
								name="title"
								label="Title*"
								control={control}
								errors={errors}
							/>
						</Box>
						<Box>
							<TextFieldController
								name="amount"
								label="Amount ($)*"
								control={control}
								errors={errors}
								type="number"
							/>
						</Box>
						<Box>
							<DropdownController
								name="status"
								label="Status*"
								options={FUNDING_STATUSES}
								control={control}
								errors={errors}
							/>
						</Box>
						<Box>
							<TextFieldController
								name="source"
								label="Source*"
								control={control}
								errors={errors}
								placeholder="e.g. Federal — DOJ COPS Office"
							/>
						</Box>
						<Box>
							<TextFieldController
								name="administrator"
								label="Administrator*"
								control={control}
								errors={errors}
								placeholder="Name of responsible party"
							/>
						</Box>
						<Box>
							<DatePickerController
								name="startDate"
								label="Start Date"
								control={control}
								errors={errors}
							/>
						</Box>
						<Box>
							<DatePickerController
								name="endDate"
								label="End Date"
								control={control}
								errors={errors}
							/>
						</Box>
						<Box sx={{ gridColumn: "1 / -1" }}>
							<TextFieldController
								name="description"
								label="Description"
								control={control}
								errors={errors}
								multiline
								rows={3}
								placeholder="Purpose, restrictions, eligible expenditures…"
							/>
						</Box>
					</Box>
				</Box>
			</DialogContent>

			<DialogActions sx={{ px: 3, pb: 2 }}>
				{!viewOnly && (
					<PATSaveButton
						onClick={handleSubmit(onSubmit)}
						tooltipText="Save"
						disabled={!isValid}
					/>
				)}
				<PATCloseButton onClick={onClose} tooltipText="Close" />
			</DialogActions>
		</Dialog>
	);
};

export default AddEditFundingSourceDialog;
